import React, { useState, useEffect } from "react";
import styled from "styled-components";

/**
 * @component MoleculeTitleCorrection
 * @description 기존 콘텐츠 제목을 수정하는 모달 내 입력 컴포넌트
 * 기능: 초기 제목을 보여주고 사용자가 수정할 때만 확인 버튼 활성화
 */

const Container = styled.div`
  width: 720px;
  height: 364;
  padding: 40px;
  background: white;
  box-shadow: 10px 10px 40px rgba(0, 0, 0, 0.10);
  border-radius: 20px;
  outline: 1px #E0E4EB solid;
  outline-offset: -1px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 32px;
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleLabel = styled.span`
  color: var(--color-text-bolder, #131416);
  font-size: 24px;
  font-weight: 700;
  line-height: 36px;
`;

const EditIconWrapper = styled.div`
  width: 16px;
  height: 16px;
  padding: 3.2px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: default;
`;

const TextAreaWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 120px;
  padding: 16px;
  border-radius: 8px;
  outline: 1px #D9DBDD solid;
  outline-offset: -1px;
  resize: none;
  font-size: 20px;
  font-weight: 500;
  color: var(--color-text-subtle, #464C53);
  line-height: 30px;
  font-family: Pretendard;
`;

const HintRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Hint = styled.span`
  color: var(--color-text-subtle, #464C53);
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

const CharCount = styled.span`
  color: ${({ isOverLimit }) => (isOverLimit ? '#FF3B30' : 'var(--color-text-subtle, #464C53)')};
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
`;

const ButtonRow = styled.div`
  width: 100%;
  display: flex;
  gap: 16px;
`;

const ButtonPrimary = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: ${({ disabled }) => (disabled ? '#9E9E9E' : '#256EF4')};
  border: none;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  outline: none;
  box-shadow: none;
  
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
    border: none;
  }

  /* 파이어폭스 내부 여백 제거 */
  &::-moz-focus-inner {
    border: 0;
    outline: none;
  }
`;

const ButtonCancel = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: ${({ disabled }) => (disabled ? '#ffffff' : '#ffffff')};
  border: 1px solid #256EF4;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  outline: none;
  box-shadow: none;
  
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
  }

  /* 파이어폭스 내부 여백 제거 */
  &::-moz-focus-inner {
    border: 0;
    outline: none;
  }
`;

const ButtonDisabled = styled(ButtonPrimary)`
  background: var(--Grays-Gray-5, #E5E5EA);
  border: none;
  outline: none;
  box-shadow: none;
  cursor: not-allowed;
  
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    outline: none;
    box-shadow: none;
    border: none;
  }
`;

const ButtonText = styled.span`
  color: ${({ disabled }) => (disabled ? '#9E9E9E' : '#FFFFFF')};
  font-size: 17px;
  font-weight: 400;
  line-height: 26px;
`;

const ButtonText2 = styled.span`
  color: ${({ disabled }) => (disabled ? '#9E9E9E' : '#256EF4')};
  font-size: 17px;
  font-weight: 400;
  line-height: 26px;
`;

export default function MoleculeTitleCorrection({
  initialTitle = '',
  onCancel = () => {},
  onConfirm = (newTitle) => {},
  headerTitle = "콘텐츠명 수정"
}) {
  const [title, setTitle] = useState(initialTitle);
  const [dirty, setDirty] = useState(false);
  const MAX_LENGTH = 50;

  useEffect(() => {
    setDirty(title.trim() !== initialTitle.trim());
  }, [title, initialTitle]);

  const isOverLimit = title.length > MAX_LENGTH;
  const canConfirm = dirty && !isOverLimit && title.trim().length > 0;

  return (
    <Container>
      <Row>
        <TitleLabel>{headerTitle}</TitleLabel>
        <EditIconWrapper>
          {/* 편집 아이콘 삽입 혹은 SVG */}
        </EditIconWrapper>
      </Row>

      <TextAreaWrapper>
        <TextArea
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="50자 이내로 작성해주세요."
          maxLength={MAX_LENGTH}
        />
        <HintRow>
          <Hint>50자 이내로 작성해주세요.</Hint>
          <CharCount isOverLimit={isOverLimit}>
            {title.length}/{MAX_LENGTH}
          </CharCount>
        </HintRow>
      </TextAreaWrapper>

      <ButtonRow>
        <ButtonCancel onClick={onCancel}>
          <ButtonText2>취소</ButtonText2>
        </ButtonCancel>
        {canConfirm ? (
          <ButtonPrimary onClick={() => onConfirm(title)}>
            <ButtonText>확인</ButtonText>
          </ButtonPrimary>
        ) : (
          <ButtonDisabled disabled>
            <ButtonText disabled>확인</ButtonText>
          </ButtonDisabled>
        )}
      </ButtonRow>
    </Container>
  );
}
